import React, { useState, useEffect, useRef } from 'react'
import { Row, Col, Container, Modal, Form, Button, Badge } from 'react-bootstrap'
import Card from '../../../components/Card'
import { InView } from "react-intersection-observer";
import { useNavigate } from 'react-router-dom'
import { apiHandler } from '../../../api/apihandler';
import {
   TEACHER_STUDENT_LEAVE_APPLICATIONS_API,
   LEAVE_APPLICATION_APPROVE_API,
   LEAVE_APPLICATION_REJECT_API
} from "../../../api/constants";
import LoaderModal from '../../../components/loaderModal';
import Swal from 'sweetalert2'
import Moment from 'moment';
import SignatureCanvas from 'react-signature-canvas';
import { handleImageUpload } from '../../../api/uploads';

// image
import loader from '../../../assets/images/page-img/page-load-loader.gif'
import NoDataFound from '../../../components/noDataFound';

const LEAVE_TYPE_META = {
   sick: { icon: 'ri-heart-pulse-line', label: 'Sick Leave' },
   vacation: { icon: 'ri-flight-takeoff-line', label: 'Vacation' },
   emergency: { icon: 'ri-alarm-warning-line', label: 'Emergency Leave' },
   family: { icon: 'ri-parent-line', label: 'Family Leave' },
   other: { icon: 'ri-calendar-line', label: 'Other' },
};

const STATUS_META = {
   0: { label: 'Pending', bg: 'bg-soft-warning', text: 'text-warning', badge: 'warning' },
   1: { label: 'Approved', bg: 'bg-soft-primary', text: 'text-primary', badge: 'primary' },
   2: { label: 'Rejected', bg: 'bg-soft-danger', text: 'text-danger', badge: 'danger' },
};

const DEFAULT_AVATAR = "https://uzone360live.s3.amazonaws.com/noimage.png";
const getStudentPhoto = (student) => student?.user_setting?.photo || student?.photo || DEFAULT_AVATAR;

const getLeaveTypeMeta = (type) => LEAVE_TYPE_META[type] || LEAVE_TYPE_META.other;
const getStatusMeta = (status) => STATUS_META[status] || STATUS_META[0];

const TeacherLeaveApplications = () => {

   const navigate = useNavigate();
   const [leaveApplicationList, setLeaveApplicationList] = useState([]);
   const [page, setPage] = useState(1);
   const [maxPage, setMaxPage] = useState(1);
   const [showLoader, setShowLoader] = useState(false);
   const [showMoreDataLoader, setShowMoreDataLoader] = useState(false);

   // Filters
   const [statusFilter, setStatusFilter] = useState('');
   const [leaveTypeFilter, setLeaveTypeFilter] = useState('');
   const [fromDateFilter, setFromDateFilter] = useState('');
   const [toDateFilter, setToDateFilter] = useState('');
   const [appliedFilters, setAppliedFilters] = useState({ status: '', leave_type: '', from_date: '', to_date: '' });

   // Detail modal
   const [showDetailModal, setShowDetailModal] = useState(false);
   const [activeLeave, setActiveLeave] = useState(null);
   const [decisionMode, setDecisionMode] = useState(null); // null | 'approve' | 'reject'
   const [adminComments, setAdminComments] = useState('');
   const [isSubmittingDecision, setIsSubmittingDecision] = useState(false);

   const signaturePadRef = useRef(null);

   const [isFirst, setIsFirst] = useState(true);
   useEffect(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setIsFirst(false)
   }, [isFirst]);

   useEffect(() => {
      if (localStorage.getItem("uzoneAuthToken")) {
         setLeaveApplicationList([])
         setPage(1)
         getLeaveApplicationList(1)
      } else {
         navigate('/')
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [appliedFilters]);

   const loadMore = (inView) => {
      if (inView) {
         if (page < maxPage) {
            getLeaveApplicationList(page + 1)
            setPage(page + 1)
         }
      }
   };

   async function getLeaveApplicationList(pageVal) {
      if (pageVal === 1) {
         setShowLoader(true)
      } else {
         setShowMoreDataLoader(true)
      }
      try {
         const res = await apiHandler(TEACHER_STUDENT_LEAVE_APPLICATIONS_API, "POST", {
            page: pageVal,
            limit: 10,
            ...appliedFilters
         })
         if (res.data?.response?.status === 200) {
            setMaxPage(res?.data?.data?.data?.meta?.last_page)
            if (res?.data?.data?.data?.meta?.total > 0) {
               if (pageVal === 1) {
                  setLeaveApplicationList(res?.data?.data?.data?.data)
               } else {
                  setLeaveApplicationList(leaveApplicationList.concat(res?.data?.data?.data?.data))
               }
            } else if (pageVal === 1) {
               setLeaveApplicationList([])
            }
         } else {
            Swal.fire({
               title: 'Error',
               text: res?.response?.data?.data?.message || 'Failed to load leave applications',
               icon: 'error',
               confirmButtonText: 'Okay'
            })
         }
      } catch (error) {
         console.error(error);
         Swal.fire({
            title: 'Error',
            text: 'Something went wrong. Please try again.',
            icon: 'error',
            confirmButtonText: 'Okay'
         })
      }
      finally {
         setShowMoreDataLoader(false)
         setShowLoader(false)
      }
   }

   const applyFilters = () => {
      setAppliedFilters({
         status: statusFilter,
         leave_type: leaveTypeFilter,
         from_date: fromDateFilter,
         to_date: toDateFilter
      });
   };

   const clearFilters = () => {
      setStatusFilter(''); setLeaveTypeFilter(''); setFromDateFilter(''); setToDateFilter('');
      setAppliedFilters({ status: '', leave_type: '', from_date: '', to_date: '' });
   };

   const openDetailModal = (leave, initialDecision = null) => {
      setActiveLeave(leave);
      setDecisionMode(initialDecision);
      setAdminComments('');
      setShowDetailModal(true);
   };

   const closeDetailModal = () => {
      setShowDetailModal(false);
      setActiveLeave(null);
      setDecisionMode(null);
      setAdminComments('');
      if (signaturePadRef.current) {
         signaturePadRef.current.clear();
      }
   };

   const handleClearSignature = () => {
      if (signaturePadRef.current) {
         signaturePadRef.current.clear();
      }
   };

   const goToStudentProfile = (student, e) => {
      if (e) e.stopPropagation();
      if (!student?.id) return;
      navigate(`/dashboard/app/friend-profile/${student.id}`);
   };

   const refreshAfterDecision = () => {
      closeDetailModal();
      setLeaveApplicationList([])
      setPage(1)
      getLeaveApplicationList(1)
   };

   async function handleApprove() {
      if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
         Swal.fire({ title: 'Warning', text: 'Please provide your signature before approving.', icon: 'warning', confirmButtonText: 'Okay' })
         return;
      }

      setIsSubmittingDecision(true)
      try {
         const signatureDataURL = signaturePadRef.current.toDataURL('image/png');
         const signatureBlob = await fetch(signatureDataURL).then(res => res.blob());
         const signatureFile = new File([signatureBlob], `admin_signature_${Date.now()}.png`, { type: 'image/png' });
         const signatureUrl = await handleImageUpload(signatureFile, 'signatures/', 'image/png');

         const res = await apiHandler(LEAVE_APPLICATION_APPROVE_API, "POST", {
            id: activeLeave?.id,
            admin_comments: adminComments.trim(),
            admin_signature: signatureUrl
         })
         if (res.data?.response?.status === 200) {
            Swal.fire({ title: 'Success', text: res?.data?.data?.message || 'Leave application approved successfully', icon: 'success', confirmButtonText: 'Okay' })
            refreshAfterDecision();
         } else {
            const errorText = res?.response?.data?.data?.errors?.[0] || res?.response?.data?.data?.message || 'Failed to approve leave application';
            Swal.fire({
               title: 'Error',
               text: errorText,
               icon: 'error',
               confirmButtonText: 'Okay'
            })
            if (errorText === 'Leave application is not in pending status') {
               refreshAfterDecision();
            }
         }
      } catch (error) {
         console.error(error);
         Swal.fire({ title: 'Error', text: 'Something went wrong. Please try again.', icon: 'error', confirmButtonText: 'Okay' })
      }
      finally {
         setIsSubmittingDecision(false)
      }
   }

   async function handleReject() {
      if (!adminComments || adminComments.trim() === '') {
         Swal.fire({ title: 'Warning', text: 'Please provide a reason for rejecting this application.', icon: 'warning', confirmButtonText: 'Okay' })
         return;
      }

      setIsSubmittingDecision(true)
      try {
         const res = await apiHandler(LEAVE_APPLICATION_REJECT_API, "POST", {
            id: activeLeave?.id,
            admin_comments: adminComments.trim()
         })
         if (res.data?.response?.status === 200) {
            Swal.fire({ title: 'Success', text: res?.data?.data?.message || 'Leave application rejected successfully', icon: 'success', confirmButtonText: 'Okay' })
            refreshAfterDecision();
         } else {
            const errorText = res?.response?.data?.data?.errors?.[0] || res?.response?.data?.data?.message || 'Failed to reject leave application';
            Swal.fire({
               title: 'Error',
               text: errorText,
               icon: 'error',
               confirmButtonText: 'Okay'
            })
            if (errorText === 'Leave application is not in pending status') {
               refreshAfterDecision();
            }
         }
      } catch (error) {
         console.error(error);
         Swal.fire({ title: 'Error', text: 'Something went wrong. Please try again.', icon: 'error', confirmButtonText: 'Okay' })
      }
      finally {
         setIsSubmittingDecision(false)
      }
   }

   const pendingCount = leaveApplicationList?.filter(leave => leave.status === 0).length || 0;
   const approvedCount = leaveApplicationList?.filter(leave => leave.status === 1).length || 0;
   const rejectedCount = leaveApplicationList?.filter(leave => leave.status === 2).length || 0;

   const statCards = [
      { label: 'Pending', value: pendingCount, icon: 'ri-time-line', bg: 'bg-soft-warning', text: 'text-warning' },
      { label: 'Approved', value: approvedCount, icon: 'ri-checkbox-circle-line', bg: 'bg-soft-primary', text: 'text-primary' },
      { label: 'Rejected', value: rejectedCount, icon: 'ri-close-circle-line', bg: 'bg-soft-danger', text: 'text-danger' },
      { label: 'Total', value: leaveApplicationList?.length || 0, icon: 'ri-file-list-3-line', bg: 'bg-light', text: 'text-dark' },
   ];

   return (
      <>
         <LoaderModal showLoader={showLoader} />
         <div id='content-page' className='content-page scroller'>
            <Container>
               <Row>
                  <Col lg={12}>

                     {/* Header + Stat cards */}
                     <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <div>
                           <h4 className="mb-0">Student Leave Applications</h4>
                           <p className="text-muted mb-0" style={{ fontSize: '13px' }}>Review and act on leave requests from your students</p>
                        </div>
                     </div>

                     <Row className="g-3 mb-3">
                        {statCards.map((s) => (
                           <Col xs={6} md={3} key={s.label}>
                              <Card className="mb-0 h-100">
                                 <Card.Body className="d-flex align-items-center gap-3 py-3">
                                    <div className={`d-flex align-items-center justify-content-center rounded-circle ${s.bg}`} style={{ width: '48px', height: '48px', flexShrink: 0 }}>
                                       <i className={`${s.icon} ${s.text}`} style={{ fontSize: '22px' }}></i>
                                    </div>
                                    <div>
                                       <h3 className={`mb-0 fw-bold ${s.text}`}>{s.value}</h3>
                                       <p className="mb-0 text-muted small">{s.label}</p>
                                    </div>
                                 </Card.Body>
                              </Card>
                           </Col>
                        ))}
                     </Row>

                     {/* Filters */}
                     <Card className="mb-3">
                        <Card.Body className="py-3">
                           <Row className="g-2 align-items-end">
                              <Col xs={6} md={3}>
                                 <Form.Label className="small text-muted mb-1">Status</Form.Label>
                                 <Form.Select size="sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                    <option value="">All Status</option>
                                    <option value="0">Pending</option>
                                    <option value="1">Approved</option>
                                    <option value="2">Rejected</option>
                                 </Form.Select>
                              </Col>
                              <Col xs={6} md={3}>
                                 <Form.Label className="small text-muted mb-1">Leave Type</Form.Label>
                                 <Form.Select size="sm" value={leaveTypeFilter} onChange={(e) => setLeaveTypeFilter(e.target.value)}>
                                    <option value="">All Types</option>
                                    <option value="sick">Sick</option>
                                    <option value="vacation">Vacation</option>
                                    <option value="emergency">Emergency</option>
                                    <option value="family">Family</option>
                                    <option value="other">Other</option>
                                 </Form.Select>
                              </Col>
                              <Col xs={6} md={2}>
                                 <Form.Label className="small text-muted mb-1">From Date</Form.Label>
                                 <Form.Control size="sm" type="date" value={fromDateFilter} onChange={(e) => setFromDateFilter(e.target.value)} />
                              </Col>
                              <Col xs={6} md={2}>
                                 <Form.Label className="small text-muted mb-1">To Date</Form.Label>
                                 <Form.Control size="sm" type="date" value={toDateFilter} onChange={(e) => setToDateFilter(e.target.value)} />
                              </Col>
                              <Col xs={12} md={2} className="d-flex gap-2">
                                 <Button variant="outline-secondary" size="sm" onClick={clearFilters} className="w-100">Clear</Button>
                                 <Button variant="primary" size="sm" onClick={applyFilters} className="w-100">
                                    <i className="ri-search-line"></i> Apply
                                 </Button>
                              </Col>
                           </Row>
                        </Card.Body>
                     </Card>

                     {/* List */}
                     <Card>
                        <Card.Body className="p-0">
                           <div className="p-3">
                              {leaveApplicationList?.length > 0 ? leaveApplicationList.map((elm) => {
                                 const leaveMeta = getLeaveTypeMeta(elm?.leave_type);
                                 const statusMeta = getStatusMeta(elm?.status);
                                 return (
                                    <div key={elm?.id} className="d-flex align-items-center justify-content-between flex-wrap gap-3 p-3 mb-2 rounded"
                                       style={{ border: '1px solid #eef0f3', transition: 'box-shadow 0.15s ease' }}
                                       onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.06)'}
                                       onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                                    >
                                       <div className="d-flex align-items-center gap-3" style={{ minWidth: '260px', flex: 1 }}>
                                          <img
                                             src={getStudentPhoto(elm?.student)}
                                             alt={`${elm?.student?.first_name || ''} ${elm?.student?.last_name || ''}`}
                                             onClick={(e) => goToStudentProfile(elm?.student, e)}
                                             onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                                             title="View profile"
                                             className="rounded-circle"
                                             style={{ width: '46px', height: '46px', flexShrink: 0, objectFit: 'cover', cursor: 'pointer', border: '1px solid #e5e7eb' }}
                                          />
                                          <div>
                                             <h6 className="mb-0 fw-bold" style={{ cursor: 'pointer' }} onClick={(e) => goToStudentProfile(elm?.student, e)}>
                                                {elm?.student?.first_name} {elm?.student?.last_name}
                                             </h6>
                                             <div className="d-flex align-items-center gap-2 mt-1">
                                                <span className="text-muted small"><i className={`${leaveMeta.icon} me-1`}></i>{leaveMeta.label}</span>
                                                <span className="text-muted small">·</span>
                                                <span className="text-muted small">
                                                   {Moment(elm?.from_date).format('MMM D')}{elm?.from_date !== elm?.to_date ? ` - ${Moment(elm?.to_date).format('MMM D, YYYY')}` : `, ${Moment(elm?.from_date).format('YYYY')}`}
                                                </span>
                                                <span className="badge bg-light text-dark">{elm?.total_days} {elm?.total_days === 1 ? 'day' : 'days'}</span>
                                             </div>
                                          </div>
                                       </div>

                                       <div className="d-flex align-items-center gap-2">
                                          <Badge bg={statusMeta.badge} className="px-3 py-2">{statusMeta.label}</Badge>
                                          {elm?.status === 0 && (
                                             <>
                                                <Button variant="success" size="sm" onClick={() => openDetailModal(elm, 'approve')}>
                                                   <i className="ri-check-line"></i> Approve
                                                </Button>
                                                <Button variant="outline-danger" size="sm" onClick={() => openDetailModal(elm, 'reject')}>
                                                   <i className="ri-close-line"></i> Reject
                                                </Button>
                                             </>
                                          )}
                                          <Button variant="light" size="sm" onClick={() => openDetailModal(elm)} title="View Details">
                                             <i className="ri-eye-line"></i>
                                          </Button>
                                       </div>
                                    </div>
                                 );
                              }) : (
                                 <div className="text-center py-5">
                                    <NoDataFound />
                                 </div>
                              )}
                              {
                                 showMoreDataLoader ?
                                    <div className="text-center py-3">
                                       <img src={loader} alt="loader" style={{ height: "80px" }} />
                                    </div> : null
                              }
                              <InView as="div" onChange={(inView, entry) => loadMore(inView)} />
                           </div>
                        </Card.Body>
                     </Card>
                  </Col>
               </Row>
            </Container>
         </div>

         {/* Detail Modal */}
         <Modal show={showDetailModal} onHide={closeDetailModal} centered size="lg">
            {activeLeave && (() => {
               const leaveMeta = getLeaveTypeMeta(activeLeave?.leave_type);
               const statusMeta = getStatusMeta(activeLeave?.status);
               return (
                  <>
                     <Modal.Header closeButton style={{ borderBottom: '1px solid #eef0f3' }}>
                        <Modal.Title style={{ fontSize: '16px', fontWeight: '600' }}>
                           <i className={`${leaveMeta.icon} me-2 ${statusMeta.text}`}></i>
                           Leave Application #{String(activeLeave?.id).padStart(4, '0')}
                        </Modal.Title>
                     </Modal.Header>
                     <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto', background: '#f8fafc' }}>

                        {/* Status banner */}
                        <div className={`rounded p-3 mb-3 ${statusMeta.bg}`}>
                           <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <div>
                                 <Badge bg={statusMeta.badge} className="mb-1">{statusMeta.label}</Badge>
                                 <div className="small text-muted">
                                    Submitted {Moment(activeLeave?.created_at).format('MMM D, YYYY [at] h:mm A')}
                                    {activeLeave?.admin_action_date && (
                                       <> · {statusMeta.label} {Moment(activeLeave?.admin_action_date).format('MMM D, YYYY [at] h:mm A')}</>
                                    )}
                                 </div>
                              </div>
                              <div className="text-end">
                                 <div className="fw-bold">{activeLeave?.total_days} {activeLeave?.total_days === 1 ? 'Day' : 'Days'}</div>
                                 <div className="small text-muted">
                                    {Moment(activeLeave?.from_date).format('MMM D')} - {Moment(activeLeave?.to_date).format('MMM D, YYYY')}
                                 </div>
                              </div>
                           </div>
                        </div>

                        <Row className="g-3 mb-3">
                           <Col md={7}>
                              <div className="bg-white rounded p-3 h-100" style={{ border: '1px solid #e5e7eb' }}>
                                 <div className="d-flex align-items-center gap-2 mb-3">
                                    <img
                                       src={getStudentPhoto(activeLeave?.student)}
                                       alt={`${activeLeave?.student?.first_name || ''} ${activeLeave?.student?.last_name || ''}`}
                                       onClick={(e) => goToStudentProfile(activeLeave?.student, e)}
                                       onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                                       title="View profile"
                                       className="rounded-circle"
                                       style={{ width: '38px', height: '38px', objectFit: 'cover', cursor: 'pointer', border: '1px solid #e5e7eb' }}
                                    />
                                    <div>
                                       <h6 className="mb-0 fw-bold" style={{ cursor: 'pointer' }} onClick={(e) => goToStudentProfile(activeLeave?.student, e)}>
                                          {activeLeave?.student?.first_name} {activeLeave?.student?.last_name}
                                       </h6>
                                       <span className="text-muted small">{leaveMeta.label}</span>
                                    </div>
                                 </div>
                                 <div className="mb-1" style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', fontWeight: 600 }}>Reason</div>
                                 <div className="p-2 rounded bg-light" style={{ fontSize: '14px' }}>
                                    {activeLeave?.reason || 'N/A'}
                                 </div>

                                 {activeLeave?.attachment && (
                                    <div className="mt-3">
                                       <div className="mb-1" style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', fontWeight: 600 }}>Attachment</div>
                                       <a href={activeLeave.attachment} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                                          <i className="ri-download-2-line me-1"></i> View Attachment
                                       </a>
                                    </div>
                                 )}
                              </div>
                           </Col>
                           <Col md={5}>
                              <div className="bg-white rounded p-3 h-100" style={{ border: '1px solid #e5e7eb' }}>
                                 <div className="mb-2" style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', fontWeight: 600 }}>Timeline</div>
                                 <div className="d-flex gap-2 mb-3">
                                    <div className="rounded-circle bg-primary" style={{ width: '8px', height: '8px', marginTop: '5px', flexShrink: 0 }}></div>
                                    <div>
                                       <div className="small fw-bold">Application Submitted</div>
                                       <div className="text-muted" style={{ fontSize: '12px' }}>{Moment(activeLeave?.created_at).format('MMM D, YYYY h:mm A')}</div>
                                    </div>
                                 </div>
                                 {activeLeave?.admin_action_date && (
                                    <div className="d-flex gap-2">
                                       <div className={`rounded-circle ${activeLeave?.status === 1 ? 'bg-primary' : 'bg-danger'}`} style={{ width: '8px', height: '8px', marginTop: '5px', flexShrink: 0 }}></div>
                                       <div>
                                          <div className="small fw-bold">Application {statusMeta.label}</div>
                                          <div className="text-muted" style={{ fontSize: '12px' }}>
                                             By {activeLeave?.admin?.first_name || 'You'} {activeLeave?.admin?.last_name || ''}
                                          </div>
                                          <div className="text-muted" style={{ fontSize: '12px' }}>{Moment(activeLeave?.admin_action_date).format('MMM D, YYYY h:mm A')}</div>
                                       </div>
                                    </div>
                                 )}
                              </div>
                           </Col>
                        </Row>

                        {(activeLeave?.parent_signature || activeLeave?.admin_signature || activeLeave?.admin_comments) && (
                           <Row className="g-3 mb-3">
                              {activeLeave?.parent_signature && (
                                 <Col md={6}>
                                    <div className="bg-white rounded p-3 h-100" style={{ border: '1px solid #e5e7eb' }}>
                                       <div className="mb-2" style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', fontWeight: 600 }}>Parent/Student Signature</div>
                                       <div className="border rounded" style={{ height: '90px' }}>
                                          <img src={activeLeave.parent_signature} alt="Signature" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                       </div>
                                    </div>
                                 </Col>
                              )}
                              {(activeLeave?.admin_signature || activeLeave?.admin_comments) && (
                                 <Col md={6}>
                                    <div className="bg-white rounded p-3 h-100" style={{ border: '1px solid #e5e7eb' }}>
                                       <div className="mb-2" style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', fontWeight: 600 }}>Your Response</div>
                                       {activeLeave?.admin_signature && (
                                          <div className="border rounded mb-2" style={{ height: '90px' }}>
                                             <img src={activeLeave.admin_signature} alt="Signature" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                          </div>
                                       )}
                                       {activeLeave?.admin_comments && (
                                          <div className="p-2 rounded bg-light" style={{ fontSize: '13px' }}>{activeLeave.admin_comments}</div>
                                       )}
                                    </div>
                                 </Col>
                              )}
                           </Row>
                        )}

                        {/* Approve/Reject action panel */}
                        {activeLeave?.status === 0 && (
                           <div className="bg-white rounded p-3" style={{ border: '1px solid #e5e7eb' }}>
                              {decisionMode === null && (
                                 <div className="d-flex gap-2">
                                    <Button variant="success" onClick={() => setDecisionMode('approve')}>
                                       <i className="ri-check-line"></i> Approve Application
                                    </Button>
                                    <Button variant="outline-danger" onClick={() => setDecisionMode('reject')}>
                                       <i className="ri-close-line"></i> Reject Application
                                    </Button>
                                 </div>
                              )}

                              {decisionMode === 'approve' && (
                                 <div>
                                    <h6 className="fw-bold text-success mb-3"><i className="ri-checkbox-circle-line"></i> Approve Leave Application</h6>
                                    <Form.Group className="mb-3">
                                       <Form.Label className="small">Comments (Optional)</Form.Label>
                                       <Form.Control as="textarea" rows={2} value={adminComments} onChange={(e) => setAdminComments(e.target.value)} placeholder="Add any comments regarding the approval..." />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                       <Form.Label className="small">Your Signature <span className="text-danger">*</span></Form.Label>
                                       <div className="border rounded bg-white" style={{ width: '100%', height: '150px' }}>
                                          <SignatureCanvas ref={signaturePadRef} canvasProps={{ className: 'signature-canvas', style: { width: '100%', height: '100%' } }} />
                                       </div>
                                       <Button variant="outline-secondary" size="sm" className="mt-2" onClick={handleClearSignature} type="button">Clear Signature</Button>
                                    </Form.Group>
                                    <div className="d-flex gap-2">
                                       <Button variant="secondary" size="sm" onClick={() => setDecisionMode(null)} disabled={isSubmittingDecision}>Back</Button>
                                       <Button variant="success" size="sm" onClick={handleApprove} disabled={isSubmittingDecision}>
                                          {isSubmittingDecision ? 'Approving...' : 'Confirm Approval'}
                                       </Button>
                                    </div>
                                 </div>
                              )}

                              {decisionMode === 'reject' && (
                                 <div>
                                    <h6 className="fw-bold text-danger mb-3"><i className="ri-close-circle-line"></i> Reject Leave Application</h6>
                                    <div className="alert alert-warning py-2 small">This action cannot be undone. Please provide a reason for rejection.</div>
                                    <Form.Group className="mb-3">
                                       <Form.Label className="small">Reason for Rejection <span className="text-danger">*</span></Form.Label>
                                       <Form.Control as="textarea" rows={2} value={adminComments} onChange={(e) => setAdminComments(e.target.value)} placeholder="Please provide the reason for rejecting this application..." />
                                    </Form.Group>
                                    <div className="d-flex gap-2">
                                       <Button variant="secondary" size="sm" onClick={() => setDecisionMode(null)} disabled={isSubmittingDecision}>Back</Button>
                                       <Button variant="danger" size="sm" onClick={handleReject} disabled={isSubmittingDecision}>
                                          {isSubmittingDecision ? 'Rejecting...' : 'Confirm Rejection'}
                                       </Button>
                                    </div>
                                 </div>
                              )}
                           </div>
                        )}
                     </Modal.Body>
                     <Modal.Footer style={{ borderTop: '1px solid #eef0f3' }}>
                        <Button variant="secondary" onClick={closeDetailModal}>Close</Button>
                     </Modal.Footer>
                  </>
               );
            })()}
         </Modal>
      </>
   )
}

export default TeacherLeaveApplications;
